// =============================================================================
// MULTICOLUMN-HORIZON — Horizon `Component` that WRAPS the vendored classic Swiper engine.
// HORIZON PILOT (multicolumn twin): the Motto multicolumn's responsive mobile swiper reparented onto
// Horizon's `Component` base class (import { Component } from '@theme/component', resolved via the
// head importmap in snippets/horizon-importmap.liquid), loaded as native ESM through a section-scoped
// `<script type="module">` in sections/multicolumn-horizon.liquid — NOT the webpack src/js bundle.
// Follows the pattern of assets/slideshow-horizon.js (pilot #2) + assets/gallery-carousel-horizon.js
// (pilot #5).
//
// This element IS the `.multicolumn` root and carries the wrapper role the vendored engine observed.
// The vendored assets/multicolumn.js engine is RESPONSIVE: a ResizeObserver on the section wrapper
// mounts Swiper below 990px (first adding the .swiper/.swiper-wrapper/.swiper-slide structural classes
// onto .swiper--multicolumn/.multicolumn-list__wrapper/.multicolumn-list__item) and strips the classes
// at >= 990px. This Component reparents that exact behaviour onto its own lifecycle:
//   • connectedCallback    → ResizeObserver observing `this`; < 990 → addClasses + mount Swiper with
//                            the field-by-field vendored config (double-init guarded, `window.Swiper`
//                            readiness polled); >= 990 → destroy + removeClasses.
//   • disconnectedCallback → unobserve/disconnect the observer, clear the readiness poll, destroy the
//                            Swiper instance, strip the structural classes.
// Theme-editor section reloads re-connect the element, so no `shopify:section:load` listener is needed
// (that was the vendored engine's second init path).
//
// DELIBERATE FIX over the vendored engine (behaviour-identical, resource-correct): the vendored
// destroy path (width >= 990) only removed classes and NEVER called swiper.destroy() — every crossing
// of the 990 boundary leaked the previous Swiper instance + its listeners (and the vendored init path
// re-`new`ed Swiper on every sub-990 resize tick, stacking more). This Component stores the single
// instance, guards re-init, and calls `this.swiper?.destroy(true, true)` BEFORE removing the classes
// (on >= 990 and on disconnect). The rendered end-state is identical; the leaks are gone.
//
// INDEPENDENCE from the vendored assets/multicolumn.js (which stays live for the ORIGINAL section) —
// the vendored engine CANNOT double-mount this twin, for two independent reasons:
//   1. Its page-load init runs on `document.currentScript.parentElement` — the section wrapper that
//      contains its OWN `<script src="multicolumn.js">` tag. This twin never emits that script tag, so
//      no currentScript anchor ever points at it.
//   2. BOTH its init paths (the currentScript anchor + the document-wide `shopify:section:load`
//      listener) are gated on `section.classList.contains('multicolumn-section')`; this twin's schema
//      class is `multicolumn-horizon-section` (+ the kept `spaced-section`), so the gate rejects it
//      even when a frozen original elsewhere on the page has registered that listener.
//
// `type="module"` scripts DEFER and run AFTER Motto's classic head scripts. `swiper-bundle.min.js` is
// a classic `defer` head script (layout/theme.liquid), so `window.Swiper` is normally ready before
// this module runs; the readiness poll below is defence-in-depth (theme-editor re-injection,
// load-order drift).
//
// UNTRACKED new file (alongside component.js / utilities.js / the other *-horizon.js pilots) —
// re-verify it exists before any push; a parallel agent's git op on assets/ can remove it.
// =============================================================================
import { Component } from '@theme/component';

// The vendored engine's OWN responsive boundary (assets/multicolumn.js: `entry.contentRect.width <
// 990`). This is deliberately NOT a brand grid token (the brand breakpoints are 640/1280) — it is
// Motto's multicolumn swiper cutoff and must stay EXACTLY 990 so the twin flips between the desktop
// grid and the mobile swiper at the same width as the frozen original.
const VENDORED_SWIPER_BOUNDARY = 990;

/**
 * The multicolumn custom element. It is the `.multicolumn` root; `this.refs.swiper` is the
 * `.multicolumn-list.swiper--multicolumn` slider. The ref is only rendered when swipe-on-mobile is
 * enabled with more than one column — mirroring the vendored `querySelector('.swiper--multicolumn')`,
 * which finds nothing otherwise.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [swiper] - the `.swiper--multicolumn` slider Swiper mounts on (absent when swipe is disabled).
 *
 * @extends {Component<Refs>}
 */
export class MulticolumnHorizon extends Component {
  connectedCallback() {
    super.connectedCallback(); // populates this.refs from [ref] descendants
    if (this.#observer) return; // double-init guard (theme-editor re-connects)
    // The vendored engine observed the section wrapper (its script tag's parentElement). This element
    // is that wrapper's only block child and spans its full width, so observing `this` sees the same
    // width — the same < 990 / >= 990 flips fire at the same moments.
    this.#observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        this.#belowBoundary = entry.contentRect.width < VENDORED_SWIPER_BOUNDARY;
        if (this.#belowBoundary) {
          this.#whenSwiperReady(() => this.#initSlider());
        } else {
          this.#destroySlider();
        }
      });
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.unobserve(this);
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#swiperPoll) {
      clearInterval(this.#swiperPoll);
      this.#swiperPoll = null;
    }
    // Same teardown order as the >= 990 path: destroy the instance FIRST (the resource fix the
    // vendored engine lacked), THEN strip the structural classes.
    this.swiper?.destroy(true, true);
    this.swiper = null;
    const slider = this.refs.swiper;
    if (slider) this.#removeClasses(slider);
  }

  /** @type {any} */ swiper = null;
  #observer = null;
  #swiperPoll = null;
  #belowBoundary = false;

  /**
   * `window.Swiper` is the vendored classic global loaded via a `defer` head script — normally present
   * before this deferred module runs. Poll briefly (≈5s cap) as defence-in-depth, then give up
   * silently (the server-rendered grid stays visible either way). Only one poll runs at a time;
   * #initSlider re-checks width/connected state if a stale poll fires late.
   */
  #whenSwiperReady(callback) {
    if (window.Swiper) {
      callback();
      return;
    }
    if (this.#swiperPoll) return;
    let tries = 0;
    this.#swiperPoll = setInterval(() => {
      if (window.Swiper) {
        clearInterval(this.#swiperPoll);
        this.#swiperPoll = null;
        callback();
      } else if (++tries > 100) {
        clearInterval(this.#swiperPoll);
        this.#swiperPoll = null;
      }
    }, 50);
  }

  // Vendored addClasses, verbatim port: the slider + wrapper always get their classes; the slides only
  // when there is more than one.
  #addClasses(slider) {
    const sliderWrapper = slider.querySelector('.multicolumn-list__wrapper');
    const slides = slider.querySelectorAll('.multicolumn-list__item');

    slider.classList.add('swiper');
    if (sliderWrapper) sliderWrapper.classList.add('swiper-wrapper');
    if (slides.length > 1) {
      slides.forEach((slide) => {
        slide.classList.add('swiper-slide');
      });
    }
  }

  // Vendored removeClasses, verbatim port (incl. stripping Swiper's inline styles off each slide).
  #removeClasses(slider) {
    const sliderWrapper = slider.querySelector('.multicolumn-list__wrapper');
    const slides = slider.querySelectorAll('.multicolumn-list__item');

    slider.classList.remove('swiper');
    if (sliderWrapper) sliderWrapper.classList.remove('swiper-wrapper');
    slides.forEach((slide) => {
      slide.removeAttribute('style');
      slide.classList.remove('swiper-slide');
    });
  }

  // Vendored initSlider (width < 990): addClasses, then mount Swiper with the EXACT vendored config.
  #initSlider() {
    if (!this.isConnected || !this.#belowBoundary) return; // a stale readiness poll may fire late
    const slider = this.refs.swiper;
    if (!slider) return; // swipe off / single column — the vendored querySelector found nothing either

    this.#addClasses(slider); // the vendored engine re-ran this on every sub-boundary resize; re-adding is a no-op

    // Double-init guard (ours + Swiper's own `el.swiper` marker) — the resource-correct replacement
    // for the vendored engine re-`new`ing Swiper on every sub-990 resize tick.
    if (this.swiper || slider.swiper) return;

    // Field-by-field the vendored assets/multicolumn.js config (renderCustom bullet loop VERBATIM).
    this.swiper = new window.Swiper(slider, {
      loop: false,
      speed: 800,
      breakpoints: {
        320: {
          slidesPerView: 1,
          slidesPerGroup: 1,
          spaceBetween: 16,
        },
        750: {
          slidesPerView: 2,
          slidesPerGroup: 2,
          spaceBetween: 16,
        },
      },
      pagination: {
        el: slider.querySelector('.multicolumn__pagination'),
        clickable: true,
        type: 'custom',
        renderCustom: function (swiper, current, total) {
          let out = '';
          for (let i = 1; i < total + 1; i++) {
            if (i == current) {
              out = `${out}<span class="swiper-pagination-bullet swiper-pagination-bullet-active" tabindex="0" role="button" aria-label="Go to slide ${i}"></span>`;
            } else {
              out = `${out}<span class="swiper-pagination-bullet" tabindex="0" role="button" aria-label="Go to slide ${i}"></span>`;
            }
          }
          return out;
        },
      },
    });
  }

  // Vendored destroySlider (width >= 990) + the resource fix: destroy the stored instance FIRST
  // (deleteInstance = true, cleanStyles = true), THEN removeClasses — the vendored path skipped
  // destroy() entirely and leaked an instance on every boundary crossing. DOM end-state is identical.
  #destroySlider() {
    const slider = this.refs.swiper;
    if (!slider) return;
    this.swiper?.destroy(true, true);
    this.swiper = null;
    this.#removeClasses(slider);
  }
}

if (!customElements.get('multicolumn-horizon')) {
  customElements.define('multicolumn-horizon', MulticolumnHorizon);
}
